import { type AppStateStore } from "../state/app-state";
import type { AppStateSnapshot } from "../types";
import { getRecoveryAction } from "./permission-error";

export interface StatusOverlay {
  element: HTMLElement;
  setCtaVisible(visible: boolean): void;
  destroy(): void;
}

export interface StatusOverlayOptions {
  store: AppStateStore;
  onRetry(): void;
  onCtaClick(): void;
}

export function createStatusOverlay(options: StatusOverlayOptions): StatusOverlay {
  const element = document.createElement("aside");
  element.className = "status-overlay";
  element.setAttribute("aria-live", "polite");

  const statusLine = document.createElement("p");
  statusLine.className = "status-overlay__line";

  const webviewPanel = document.createElement("div");
  webviewPanel.className = "status-overlay__webview";
  webviewPanel.hidden = true;

  const webviewTitle = document.createElement("h2");
  webviewTitle.className = "status-overlay__webview-title";

  const webviewBody = document.createElement("p");
  webviewBody.className = "status-overlay__webview-body";

  webviewPanel.append(webviewTitle, webviewBody);

  const retryButton = document.createElement("button");
  retryButton.className = "button button--secondary status-overlay__retry";
  retryButton.type = "button";
  retryButton.textContent = "Tentar novamente";
  retryButton.hidden = true;
  retryButton.addEventListener("click", options.onRetry);

  const ctaButton = document.createElement("button");
  ctaButton.className = "button button--cta status-overlay__cta";
  ctaButton.type = "button";
  ctaButton.textContent = "Falar com a Converto";
  ctaButton.hidden = true;
  ctaButton.addEventListener("click", options.onCtaClick);

  element.append(statusLine, webviewPanel, retryButton, ctaButton);

  let ctaRequested = false;
  let currentSnapshot = options.store.getState();

  const render = (snapshot: AppStateSnapshot): void => {
    currentSnapshot = snapshot;
    statusLine.textContent = snapshot.copy.message;

    const isInternalBrowser = snapshot.status === "internal-browser";
    webviewPanel.hidden = !isInternalBrowser;
    webviewTitle.textContent = snapshot.copy.title ?? "";
    webviewBody.textContent = snapshot.copy.body ?? "";

    const action = getRecoveryAction(snapshot.status);
    retryButton.hidden = !action.visible;
    retryButton.textContent = action.label;

    ctaButton.hidden = !(ctaRequested && snapshot.status === "target-detected");
  };

  const unsubscribe = options.store.subscribe(render);

  return {
    element,
    setCtaVisible(visible: boolean): void {
      ctaRequested = visible;
      ctaButton.hidden = !(ctaRequested && currentSnapshot.status === "target-detected");
    },
    destroy(): void {
      unsubscribe();
      element.remove();
    },
  };
}
